import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const SidebarContext = createContext();

/**
 * SidebarProvider - Manages sidebar expand/collapse state
 * Default: Desktop open, Tablet/Mobile closed
 */
export const SidebarProvider = ({ children }) => {
  // Check if desktop on initial render
  const isDesktop = useCallback(() => typeof window !== 'undefined' && window.innerWidth >= 1024, []);

  // Desktop inline-push sidebar state (>=1024px only)
  const [isOpen, setIsOpen] = useState(true);
  // Below-desktop overlay drawer state - fully independent of isOpen above.
  // Never touched by any other effect (e.g. the Assistance-panel auto-collapse
  // logic in DashboardLayout only ever reads/writes isOpen, never this).
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  // Handle window resize/orientation change to reset default state
  useEffect(() => {
    const handleResize = () => {
      // Only auto-close on mobile, auto-open on desktop
      if (window.innerWidth >= 1024) {
        setIsOpen(true);
        setIsMobileDrawerOpen(false); // the drawer is meaningless once we're in desktop layout
      } else if (window.innerWidth < 768) {
        setIsOpen(false);
      }
    };

    // Set initial state based on screen size
    setIsOpen(isDesktop());

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [isDesktop]);

  const toggle = () => setIsOpen(prev => !prev);
  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);

  const toggleMobileDrawer = () => setIsMobileDrawerOpen(prev => !prev);
  const closeMobileDrawer = () => setIsMobileDrawerOpen(false);

  return (
    <SidebarContext.Provider value={{ isOpen, toggle, open, close, isMobileDrawerOpen, toggleMobileDrawer, closeMobileDrawer }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

export default SidebarContext;
